import { getOrders } from "./APIClient";
import { QUOTES_CPAP_MACHINE, QUOTES_MASK } from "./utils";

/**
 * Where patients are sent when they want to cancel. Renuma carries the
 * cancellation out from the admin area; patients cannot do it themselves.
 */
export const CANCEL_CONTACT_EMAIL = "info@renumasleep.com";

const parseQuotes = (quotes) => {
  if (typeof quotes !== "string" || quotes.indexOf("/") === -1) return null;
  const [paid, total] = quotes.split("/").map((n) => parseInt(n, 10));
  if (isNaN(paid) || isNaN(total) || total <= 0) return null;
  return { paid, total };
};

/**
 * The patient's current Renuma+ order, or null. Swallows its own errors - the
 * only caller uses it to improve a displayed figure, so a failure should leave
 * the screen working rather than break it.
 */
export const getActiveRenumaPlusOrder = async () => {
  try {
    const orders = await getOrders();
    const items = (orders && orders.Items) || [];
    const active = items.filter(
      (o) => o.type === "Renuma+" && o.subscription === "active"
    );
    if (active.length === 0) return null;
    return active.sort((a, b) => (a.date_ordered < b.date_ordered ? 1 : -1))[0];
  } catch (error) {
    console.log("Could not read Renuma+ orders:", error);
    return null;
  }
};

/**
 * Phase 2 (month 11 onward) bills the mask price / QUOTES_MASK; Phase 1 bills
 * the machine price / QUOTES_CPAP_MACHINE. Derived from the patient's own stored
 * order rather than read from `next_payment.amount`, because webhook/post.py
 * writes a hardcoded 30 at the rollover and would show $30 while Stripe charges
 * $40. Reading the order also means existing members correctly keep showing the
 * price they signed up at.
 *
 * Falls back to the stored figure on anything unexpected.
 */
export const derivePaymentAmount = (order, fallback) => {
  try {
    const subtotal = order && order.subtotal;
    const parsed = parseQuotes(
      order && order.next_payment && order.next_payment.quotes
    );
    if (!Array.isArray(subtotal) || subtotal.length < 2 || !parsed) {
      return fallback;
    }

    const machinePrice = parseInt(subtotal[0], 10);
    const maskPrice = parseInt(subtotal[1], 10);
    if (isNaN(machinePrice) || isNaN(maskPrice)) return fallback;

    // Machine paid up front: the schedule is mask instalments only, so every
    // payment is a Phase 2 payment.
    if (parsed.total === QUOTES_MASK) {
      return Math.floor(maskPrice / QUOTES_MASK);
    }
    if (parsed.total === QUOTES_CPAP_MACHINE + QUOTES_MASK) {
      return parsed.paid <= QUOTES_CPAP_MACHINE
        ? Math.floor(machinePrice / QUOTES_CPAP_MACHINE)
        : Math.floor(maskPrice / QUOTES_MASK);
    }
    return fallback;
  } catch (error) {
    console.log("Could not derive next payment amount:", error);
    return fallback;
  }
};
